import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '../../auth/context/AuthContext';
import productService from '../services/productService';
import UserDashboardStats from '../components/UserDashboardStats';

import '../styles/userDashboard.css';

const UserDashboard = () => {
  const { user, currentUser } = useAuth();
  const location = useLocation();

  const profile = user || currentUser || {};

  const displayName =
    profile.name ||
    profile.username ||
    'Distributor';

  const [stats, setStats] = useState({
    products: null,
    batches: null,
    invoices: null,
    stock: null,
  });

  const [recentProducts, setRecentProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const result = await productService.getProducts({
        page: 1,
        limit: 5,
      });

      setStats((previous) => ({
        ...previous,
        products: result.pagination?.total ?? null,
      }));

      setRecentProducts(result.products || []);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          'Unable to load dashboard products.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const result = await productService.getProducts({
          page: 1,
          limit: 5,
        });

        if (cancelled) return;

        setStats((previous) => ({
          ...previous,
          products: result.pagination?.total ?? null,
        }));

        setRecentProducts(result.products || []);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.message ||
              'Unable to load dashboard products.'
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [location.key]);

  const handleRetry = () => {
    loadDashboard();
  };

  return (
    <section className="user-dashboard">
      <div className="user-dashboard-welcome">
        <div>
          <p className="user-dashboard-eyebrow">
            DISTRIBUTOR WORKSPACE
          </p>

          <h1>Welcome back, {displayName}!</h1>

          <p>
            Manage your products and get an overview of your
            inventory workspace.
          </p>
        </div>

        <Link
          to="/user/products/create"
          className="user-dashboard-primary-button"
        >
          <span aria-hidden="true">+</span>
          Add Product
        </Link>
      </div>

      {error && (
        <div
          className="user-dashboard-alert"
          role="alert"
        >
          <strong>Dashboard data unavailable</strong>
          <p>{error}</p>

          <button
            type="button"
            onClick={handleRetry}
            disabled={loading}
          >
            {loading ? 'Retrying...' : 'Retry'}
          </button>
        </div>
      )}

      <div className="user-dashboard-section-heading">
        <div>
          <h2>Inventory Overview</h2>
          <p>
            Your product summary. Other inventory statistics
            will be available as those modules are implemented.
          </p>
        </div>
      </div>

      <UserDashboardStats
        stats={stats}
        loading={loading}
      />

      <div className="user-dashboard-content-grid">
        <section className="user-dashboard-panel">
          <div className="user-dashboard-panel-header">
            <div>
              <h2>Recent Products</h2>
              <p>Your latest products.</p>
            </div>

            <Link to="/user/products">
              View all
            </Link>
          </div>

          {loading ? (
            <div className="user-dashboard-empty">
              Loading products...
            </div>
          ) : recentProducts.length === 0 ? (
            <div className="user-dashboard-empty">
              <div className="user-dashboard-empty-icon">
                +
              </div>

              <h3>No products yet</h3>

              <p>
                Add your first product to start managing your
                inventory.
              </p>

              <Link to="/user/products/create">
                Create your first product
              </Link>
            </div>
          ) : (
            <div className="user-dashboard-recent-list">
              {recentProducts.map((product) => (
                <div
                  className="user-dashboard-product-row"
                  key={product.id}
                >
                  <div
                    className="user-dashboard-product-icon"
                    aria-hidden="true"
                  >
                    ▦
                  </div>

                  <div className="user-dashboard-product-info">
                    <strong>{product.product_name}</strong>

                    <span>
                      SKU: {product.sku}
                      {product.brand
                        ? ` · ${product.brand}`
                        : ''}
                    </span>
                  </div>

                  <span
                    className={`user-dashboard-product-status ${
                      product.is_active
                        ? 'active'
                        : 'inactive'
                    }`}
                  >
                    {product.is_active ? 'Active' : 'Inactive'}
                  </span>

                  <Link
                    to={`/user/products/${product.id}/edit`}
                    className="user-dashboard-edit-link"
                    aria-label={`Edit ${product.product_name}`}
                  >
                    Edit
                  </Link>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="user-dashboard-panel">
          <div className="user-dashboard-panel-header">
            <div>
              <h2>Quick Actions</h2>
              <p>Product management shortcuts.</p>
            </div>
          </div>

          <div className="user-dashboard-quick-actions">
            <Link
              to="/user/products"
              className="user-dashboard-action"
            >
              <span className="user-dashboard-action-icon blue">
                ▦
              </span>

              <span>
                <strong>Manage Products</strong>
                <small>View and update products</small>
              </span>

              <span className="user-dashboard-arrow">→</span>
            </Link>

            <Link
              to="/user/products/create"
              className="user-dashboard-action"
            >
              <span className="user-dashboard-action-icon green">
                +
              </span>

              <span>
                <strong>Add New Product</strong>
                <small>Register a product for your firm</small>
              </span>

              <span className="user-dashboard-arrow">→</span>
            </Link>
          </div>
        </section>
      </div>
    </section>
  );
};

export default UserDashboard;